import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  Wind,
  Droplets,
  Hand,
  CalendarCheck,
  Square,
  Waves,
  ShieldCheck,
  Leaf,
  Eye,
  Wrench,
  BadgeCheck,
  CheckCircle2,
  ArrowRight,
  Phone,
  ChevronDown,
  MapPin,
  Building2,
  Beaker,
  AlertOctagon,
} from "lucide-react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import SEO from "@/components/SEO";

const SITE_URL = "https://deepcleanrr.com";
const CANONICAL = `${SITE_URL}/services/wrightsville-beach`;

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_deep-hygiene/artifacts/bptznbfo_St.%20Petersburg%20Restroom%20Cleaning.png";
const FOAM_IMG =
  "https://customer-assets.emergentagent.com/job_deep-hygiene/artifacts/8fljp405_Foam%20Cleaning%20Tampa.png";
const MODERN_IMG =
  "https://customer-assets.emergentagent.com/job_deep-hygiene/artifacts/tyxqqhmr_Commercial%20restroom%20Cleaning.png";
const FACILITIES_IMG =
  "https://customer-assets.emergentagent.com/job_deep-hygiene/artifacts/n9a3dg4m_North Carolina%20Restroom%20Cleaning.png";

const FACILITIES = [
  "Restaurants & Bars",
  "Office Buildings",
  "Retail Stores",
  "Medical Facilities",
  "Schools & Universities",
  "Warehouses",
  "Apartment Complexes",
  "Gyms & Fitness Centers",
  "Gas Stations",
  "Industrial Facilities",
];

const FOAM_BENEFITS = [
  "Breaks down urine salts and organic buildup",
  "Eliminates odor-causing bacteria",
  "Penetrates grout lines and floor crevices",
  "Removes hidden grime and contaminants",
  "Provides deeper restroom sanitization",
  "Helps reduce cross-contamination",
  "Improves restroom appearance and cleanliness",
];

const PRESSURE_BENEFITS = [
  "Remove deep floor buildup",
  "Restore tile and grout appearance",
  "Eliminate slippery residue",
  "Sanitize restroom floors",
  "Reduce bacterial contamination",
  "Improve restroom safety and cleanliness",
];

const ODOR_SERVICES = [
  "Urine salt removal",
  "Deep foam sanitation",
  "Urinal deep cleaning",
  "Floor and grout deodorizing",
  "Odor-causing bacteria treatment",
  "Restroom smell elimination",
];

const CONTAMINANTS = ["E. coli", "Salmonella", "Staphylococcus", "Mold & Mildew Bacteria"];

const HIGH_TOUCH = [
  { icon: Hand, label: "Door handles" },
  { icon: Droplets, label: "Flush handles" },
  { icon: Sparkles, label: "Sinks & faucets" },
  { icon: Square, label: "Toilet partitions" },
  { icon: BadgeCheck, label: "Urinals" },
  { icon: ShieldCheck, label: "Toilets" },
  { icon: Waves, label: "Floors & grout lines" },
];

const WHY = [
  { icon: Waves, title: "Deep Foam Cleaning", desc: "Foam clings to surfaces long enough to penetrate grout and crevices for real sanitation." },
  { icon: Wrench, title: "High-Pressure Surface Cleaning", desc: "Industrial pressure restoration of floors, tile, and grout that mopping cannot reach." },
  { icon: Wind, title: "Urine Odor Elimination", desc: "We target the source — urine salts and bacteria — not just mask the smell." },
  { icon: Beaker, title: "Tile & Grout Sanitation", desc: "Restores texture, removes embedded contamination, and renews appearance." },
  { icon: ShieldCheck, title: "Commercial Disinfecting", desc: "Targeted treatment of E. coli, salmonella, staph, mold, and mildew bacteria." },
  { icon: BadgeCheck, title: "High-Traffic Restoration", desc: "Built specifically for heavy-use restrooms in commercial environments." },
];

const SERVICE_AREAS = [
  "Wrightsville Beach",
  "Carolina Beach",
  "Castle Hayne",
  "Leland",
  "Hampstead",
  "Ogden",
  "Cape Fear Region",
];

const FAQS = [
  {
    q: "How often should commercial restrooms be deep cleaned?",
    a: "High-traffic commercial restrooms should typically receive professional deep cleaning services regularly depending on usage volume and sanitation requirements.",
  },
  {
    q: "Is foam cleaning better than traditional mopping?",
    a: "Yes. Foam cleaning penetrates grout lines, cracks, and hard-to-reach surfaces more effectively than traditional mopping.",
  },
  {
    q: "What causes strong restroom odors?",
    a: "Most restroom odors are caused by urine salts, bacteria buildup, moisture, and contaminants trapped beneath surfaces and grout lines.",
  },
  {
    q: "Can pressure washing sanitize restroom floors?",
    a: "Pressure washing combined with professional disinfecting solutions helps remove embedded grime, bacteria, and contaminants from restroom flooring and tile surfaces.",
  },
];

const Faq = ({ q, a, idx }) => {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div data-testid={`stp-faq-${idx}`} className="border-b border-slate-200 py-5">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-left"
      >
        <span className="font-heading text-lg font-bold text-slate-900 md:text-xl">
          {q}
        </span>
        <ChevronDown
          size={20}
          strokeWidth={2}
          className={`shrink-0 text-blue-600 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <p className="mt-3 font-body text-base font-medium leading-relaxed text-slate-600">
          {a}
        </p>
      )}
    </div>
  );
};

const WrightsvilleBeachServicePage = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Commercial Restroom Cleaning & Sanitation",
    provider: {
      "@type": "LocalBusiness",
      name: "Deep Clean Restroom Services",
      telephone: "+19102369971",
      email: "info@deepcleanrr.com",
      url: SITE_URL,
    },
    areaServed: SERVICE_AREAS.map((a) => ({ "@type": "City", name: a })),
    url: CANONICAL,
    description:
      "Professional commercial restroom deep cleaning, foam sanitation, pressure washing, urine odor removal, and disinfection in Wrightsville Beach, NC.",
  };

  return (
    <main data-testid="stp-service-page" className="bg-white">
      <SEO
        title="Commercial Restroom Cleaning Wrightsville Beach, NC | Deep Clean"
        description="Deep foam restroom cleaning, pressure washing, urine odor removal, and commercial disinfecting in Wrightsville Beach & New Hanover County. Built for high-traffic facilities."
        canonical={CANONICAL}
        image={HERO_IMG}
        type="website"
        jsonLd={jsonLd}
        keywords={[
          "commercial restroom cleaning Wrightsville Beach",
          "foam restroom sanitation",
          "urine odor removal",
          "restroom pressure washing",
          "New Hanover County janitorial services",
          "commercial bathroom disinfecting",
        ]}
      />
      <Header />

      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-slate-950 pt-24">
        <div
          className="absolute inset-0 -z-10 bg-cover bg-center"
          aria-hidden="true"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div
          className="absolute inset-0 -z-10"
          aria-hidden="true"
          style={{
            background:
              "linear-gradient(180deg, rgba(5,9,19,0.86) 0%, rgba(5,9,19,0.60) 30%, rgba(5,9,19,0.45) 100%), linear-gradient(90deg, rgba(5,9,19,0.78) 0%, rgba(5,9,19,0.40) 60%, rgba(5,9,19,0.10) 100%), radial-gradient(60% 50% at 85% 20%, rgba(6,182,212,0.25), transparent 70%)",
          }}
        />
        <div className="mx-auto max-w-7xl px-6 pb-24 pt-16 md:px-12 md:pb-32 md:pt-24">
          <p className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 font-body text-xs font-semibold uppercase tracking-[0.18em] text-white/90 backdrop-blur">
            <MapPin size={14} strokeWidth={2} className="text-cyan-300" />
            Wrightsville Beach, NC · New Hanover County
          </p>
          <h1
            data-testid="stp-hero-heading"
            className="max-w-4xl font-heading text-5xl font-black leading-[0.95] tracking-tighter text-white sm:text-6xl lg:text-7xl xl:text-8xl"
          >
            Commercial Restroom
            <br />
            <span className="text-cyan-300">Cleaning & Sanitation</span>
            <br />
            in Wrightsville Beach.
          </h1>
          <p className="mt-8 max-w-3xl font-body text-base font-medium leading-relaxed text-slate-200 md:text-lg">
            Foam restroom sanitation, pressure washing, urine odor removal, and
            commercial disinfecting built for high-traffic facilities across
            Wrightsville Beach &amp; the Cape Fear region. Beyond mop-and-bucket — into
            true deep restroom cleaning.
          </p>
          <div className="mt-10 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/#book"
              data-testid="stp-book-btn"
              className="group inline-flex h-14 items-center justify-center gap-3 rounded-md bg-blue-600 px-8 font-body text-base font-bold tracking-wide text-white hover:bg-blue-700"
            >
              Schedule Service
              <ArrowRight size={18} strokeWidth={2.2} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href="tel:9102369971"
              data-testid="stp-call-btn"
              className="inline-flex h-14 items-center justify-center gap-3 rounded-md border border-white/30 px-8 font-body text-base font-bold tracking-wide text-white hover:bg-white/10"
            >
              <Phone size={18} strokeWidth={2} />
              Call 910-236-9971
            </a>
          </div>
        </div>
      </section>

      {/* INTRODUCTION */}
      <section className="border-b border-slate-200 bg-white py-16 md:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 md:grid-cols-12 md:gap-16 md:px-12">
          <div className="md:col-span-5">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — Introduction
            </p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              Trusted by Wrightsville Beach businesses
              <br />
              <span className="text-slate-400">for true deep cleaning.</span>
            </h2>
          </div>
          <div className="md:col-span-7 space-y-5 font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
            <p>
              Businesses throughout Wrightsville Beach trust professional restroom
              sanitation services to maintain clean, odor-free, and hygienic
              facilities for employees, customers, and guests. At Deep Clean we
              specialize in commercial restroom deep cleaning, foam sanitation
              cleaning, pressure washing, urine odor removal, and high-level
              disinfecting designed specifically for high-traffic commercial
              restrooms.
            </p>
            <p>
              Unlike basic janitorial cleaning, our deep restroom cleaning
              process targets hidden bacteria, urine buildup, grime, and
              odor-causing contaminants that traditional mopping leaves
              behind. We help businesses across Wrightsville Beach and the Cape Fear
              region maintain healthier, cleaner, and more professional
              restroom environments.
            </p>
          </div>
        </div>
      </section>

      {/* HIGH-TRAFFIC FACILITIES */}
      <section className="bg-slate-50 py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 md:px-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-6">
            <div className="overflow-hidden rounded-md border border-slate-200">
              <img
                src={FACILITIES_IMG}
                alt="Modern multi-stall commercial restroom with travertine walls and a row of urinals"
                data-testid="stp-facilities-image"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-6">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — For High-Traffic Facilities
            </p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              Built for the wear of constant use.
            </h2>
            <p className="mt-6 font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
              Commercial restrooms face constant exposure to moisture,
              bacteria, urine contamination, and heavy foot traffic. Floors,
              grout, urinals, toilets, partitions, and high-touch surfaces
              accumulate buildup that standard cleaning cannot eliminate.
            </p>
            <p className="mt-3 font-body text-sm font-semibold uppercase tracking-wider text-slate-500">
              Ideal for:
            </p>
            <div className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              {FACILITIES.map((f, i) => (
                <div
                  key={f}
                  data-testid={`stp-facility-${i}`}
                  className="group flex items-center gap-3 rounded-md border border-slate-200 bg-white p-3.5 transition-colors hover:border-blue-600"
                >
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-blue-600/10 text-blue-600 group-hover:bg-blue-600 group-hover:text-white">
                    <Building2 size={14} strokeWidth={2} />
                  </span>
                  <span className="font-body text-sm font-semibold text-slate-800">
                    {f}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DEEP FOAM CLEANING */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 md:px-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-6">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — Signature Service
            </p>
            <h2
              data-testid="stp-foam-heading"
              className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl"
            >
              Deep foam restroom cleaning.
            </h2>
            <p className="mt-6 font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
              Our foam system applies professional-grade sanitizing foam that
              clings to surfaces long enough to penetrate grout, floor
              textures, toilet bases, urinals, partitions, and hard-to-reach
              areas where bacteria and urine buildup hide.
            </p>
            <ul className="mt-8 space-y-3">
              {FOAM_BENEFITS.map((b, i) => (
                <li
                  key={b}
                  data-testid={`stp-foam-benefit-${i}`}
                  className="flex items-start gap-3 font-body text-base font-medium text-slate-700"
                >
                  <CheckCircle2 size={18} strokeWidth={2} className="mt-0.5 shrink-0 text-blue-600" />
                  {b}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6">
            <figure className="overflow-hidden rounded-md border border-slate-200">
              <img
                src={FOAM_IMG}
                alt="Before-and-after of a commercial toilet bowl showing dramatic improvement after deep foam cleaning"
                data-testid="stp-foam-image"
                className="h-full w-full object-cover"
              />
              <figcaption className="bg-slate-950 px-5 py-3 font-body text-xs font-bold uppercase tracking-[0.22em] text-cyan-300">
                Before &amp; After · Deep foam treatment
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* PRESSURE WASHING + ODOR REMOVAL */}
      <section className="border-y border-slate-200 bg-slate-50 py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-8 px-6 md:px-12 lg:grid-cols-2 lg:gap-10">
          {/* PRESSURE */}
          <div
            data-testid="stp-pressure-card"
            className="rounded-md border border-slate-200 bg-white p-8 md:p-10"
          >
            <span className="grid h-12 w-12 place-items-center rounded-md bg-blue-600/10 text-blue-600">
              <Wrench size={22} strokeWidth={1.6} />
            </span>
            <h2 className="mt-6 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
              Commercial Pressure Washing
            </h2>
            <p className="mt-4 font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
              Foam sanitation combined with professional restroom pressure
              washing removes embedded grime, bacteria, grease, and buildup
              from restroom floors and surfaces — restoring heavily used
              commercial bathrooms.
            </p>
            <ul className="mt-6 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              {PRESSURE_BENEFITS.map((b, i) => (
                <li
                  key={b}
                  data-testid={`stp-pressure-${i}`}
                  className="flex items-start gap-2.5 font-body text-sm font-semibold text-slate-700"
                >
                  <CheckCircle2 size={16} strokeWidth={2} className="mt-0.5 shrink-0 text-blue-600" />
                  {b}
                </li>
              ))}
            </ul>
          </div>

          {/* ODOR */}
          <div
            data-testid="stp-odor-card"
            className="relative overflow-hidden rounded-md border border-slate-200 bg-slate-950 p-8 text-white md:p-10"
          >
            <div
              className="absolute inset-0 opacity-50"
              aria-hidden="true"
              style={{
                background:
                  "linear-gradient(180deg, rgba(5,9,19,0.65) 0%, rgba(5,9,19,0.95) 100%), radial-gradient(60% 50% at 80% 0%, rgba(6,182,212,0.30), transparent 70%)",
              }}
            />
            <div className="relative">
              <span className="grid h-12 w-12 place-items-center rounded-md bg-cyan-300/15 text-cyan-300">
                <Wind size={22} strokeWidth={1.6} />
              </span>
              <h2 className="mt-6 font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
                Urine Odor Removal &amp; Deodorizing
              </h2>
              <p className="mt-4 font-body text-base font-medium leading-relaxed text-slate-300 md:text-lg">
                Persistent restroom odors come from urine salts and bacteria
                trapped beneath grout, around urinals, under toilets, and
                inside porous flooring. Most cleaners mask the smell. We
                target the source.
              </p>
              <ul className="mt-6 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                {ODOR_SERVICES.map((s, i) => (
                  <li
                    key={s}
                    data-testid={`stp-odor-${i}`}
                    className="flex items-start gap-2.5 font-body text-sm font-semibold text-white"
                  >
                    <CheckCircle2 size={16} strokeWidth={2} className="mt-0.5 shrink-0 text-cyan-300" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* DISINFECTION + MODERN IMAGE */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 md:px-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="overflow-hidden rounded-md border border-slate-200">
              <img
                src={MODERN_IMG}
                alt="Modern white-marble commercial bathroom with chrome fixtures, walk-in shower, and floating vanity"
                data-testid="stp-modern-image"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — Disinfection &amp; Germ Elimination
            </p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              Hospital-grade disinfecting.
            </h2>
            <p className="mt-6 font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
              Our restroom sanitation process reduces harmful contaminants
              commonly found in commercial bathrooms.
            </p>

            <div className="mt-8">
              <p className="font-body text-xs font-bold uppercase tracking-[0.22em] text-slate-500">
                Targeted contaminants
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {CONTAMINANTS.map((c, i) => (
                  <span
                    key={c}
                    data-testid={`stp-contaminant-${i}`}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3.5 py-1.5 font-body text-sm font-bold text-slate-800"
                  >
                    <AlertOctagon size={14} strokeWidth={2.2} className="text-blue-600" />
                    {c}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-8">
              <p className="font-body text-xs font-bold uppercase tracking-[0.22em] text-slate-500">
                Every high-touch surface
              </p>
              <div className="mt-3 grid grid-cols-1 gap-px overflow-hidden rounded-md bg-slate-200 sm:grid-cols-2">
                {HIGH_TOUCH.map((h, i) => (
                  <div
                    key={h.label}
                    data-testid={`stp-touch-${i}`}
                    className="group flex items-center gap-3 bg-white p-3.5 transition-colors hover:bg-slate-50"
                  >
                    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-blue-600/10 text-blue-600 group-hover:bg-blue-600 group-hover:text-white">
                      <h.icon size={14} strokeWidth={2} />
                    </span>
                    <span className="font-body text-sm font-semibold text-slate-800">
                      {h.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE DEEP CLEAN */}
      <section className="border-y border-slate-200 bg-slate-50 py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-12 grid grid-cols-1 items-end gap-8 md:grid-cols-12 md:gap-12">
            <div className="md:col-span-7">
              <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
                — Why Wrightsville Beach Businesses Choose Us
              </p>
              <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
                Deep sanitation. Not basic janitorial.
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
                Specialized equipment, advanced foam, and detail-driven
                technicians — focused on restroom restoration.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-px overflow-hidden rounded-md bg-slate-200 sm:grid-cols-2 lg:grid-cols-3">
            {WHY.map((r, i) => (
              <div
                key={r.title}
                data-testid={`stp-why-${i}`}
                className="group bg-white p-8 transition-colors hover:bg-slate-950"
              >
                <span className="grid h-12 w-12 place-items-center rounded-md bg-blue-600/10 text-blue-600 transition-colors group-hover:bg-cyan-300/15 group-hover:text-cyan-300">
                  <r.icon size={22} strokeWidth={1.6} />
                </span>
                <h3 className="mt-6 font-heading text-lg font-bold tracking-tight text-slate-900 transition-colors group-hover:text-white lg:text-xl">
                  {r.title}
                </h3>
                <p className="mt-3 font-body text-sm font-medium leading-relaxed text-slate-600 transition-colors group-hover:text-white/80">
                  {r.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREAS */}
      <section className="bg-white py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="mb-10 max-w-3xl">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — Service Areas
            </p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
              Serving Wrightsville Beach &amp; New Hanover County.
            </h2>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
            {SERVICE_AREAS.map((area, i) => (
              <div
                key={area}
                data-testid={`stp-area-${i}`}
                className="group flex items-center gap-2.5 rounded-md border border-slate-200 bg-white p-3 transition-colors hover:border-blue-600"
              >
                <MapPin size={14} strokeWidth={2} className="shrink-0 text-blue-600" />
                <span className="font-body text-[13px] font-semibold text-slate-800">
                  {area}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-slate-200 bg-slate-50 py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 md:px-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-4">
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-blue-600">
              — FAQ
            </p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              Frequently Asked Questions.
            </h2>
            <p className="mt-6 font-body text-base font-medium leading-relaxed text-slate-600 md:text-lg">
              Other questions? Call{" "}
              <a
                href="tel:9102369971"
                className="font-bold text-blue-600 hover:underline"
              >
                910-236-9971
              </a>
              .
            </p>
          </div>
          <div className="lg:col-span-8">
            {FAQS.map((f, i) => (
              <Faq key={f.q} q={f.q} a={f.a} idx={i} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-slate-950 py-20 text-white md:py-24">
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(60% 50% at 80% 20%, rgba(6,182,212,0.30), transparent), radial-gradient(50% 40% at 0% 100%, rgba(0,85,255,0.40), transparent)",
          }}
          aria-hidden="true"
        />
        <div className="relative mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-6 md:px-12 lg:flex-row lg:items-center">
          <div>
            <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-cyan-300">
              — Schedule Service · Wrightsville Beach, NC
            </p>
            <h2 className="mt-4 max-w-2xl font-heading text-3xl font-black tracking-tighter text-white sm:text-4xl lg:text-5xl">
              Restroom odors, stained grout, or heavy use? We'll restore it.
            </h2>
          </div>
          <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
            <Link
              to="/#book"
              data-testid="stp-cta-book-btn"
              className="group inline-flex h-14 items-center justify-center gap-3 rounded-md bg-blue-600 px-8 font-body text-base font-bold tracking-wide text-white hover:bg-blue-700"
            >
              Book a Cleaning
              <ArrowRight size={18} strokeWidth={2.2} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href="tel:9102369971"
              data-testid="stp-cta-call-btn"
              className="inline-flex h-14 items-center justify-center gap-3 rounded-md border border-white/30 px-8 font-body text-base font-bold tracking-wide text-white hover:bg-white/10"
            >
              <Phone size={18} strokeWidth={2} />
              910-236-9971
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default WrightsvilleBeachServicePage;
